// The tensor app's frame. Phone: the workbook's name on top (tap: the list
// of workbooks), the screen, and tabs at the bottom (요약 · 거래 · 보기 ·
// 텐서 · 표 · 설정). PC (wide): the workbooks in a sidebar and the tabs on top.
// Screens are addresses (/, /rows, /views, /views/<id> (a saved view),
// /tensors, /tensors/<id>, /grid, /settings and under 설정 /budgets,
// /items, /csv), so the back button goes back between them; an open sheet
// takes the back button first (design 7.3). /w/<id>[/v/<view>] opens a
// workbook from a link (useAddress, design 00 7). On a phone the bottom tabs
// make room while the keyboard is up, and the back key that puts the
// keyboard away ends the typing (lib/keyboard.ts).
import { useEffect, useState } from 'react'

import { hasCoarsePointer } from '../../lib/ime'
import { watchKeyboard } from '../../lib/keyboard'
import { Icon, Sheet, Spinner } from '../../ui/common'
import { goBack, navigate, useMedia, usePath } from '../../ui/router'
import { Toast, showToast } from '../../ui/toast'
import type { Id } from '../api'
import { engine, lastWorkbook } from '../engine'
import { monthOf } from '../format'
import { findView } from '../pivot'
import { getState, useT } from '../store'
import { BudgetsScreen } from './Budgets'
import { VisibilityBadge, useCurrent } from './common'
import { CsvScreen } from './Csv'
import { GridScreen } from './Grid'
import { ItemsScreen } from './Items'
import { LoginScreen } from './Login'
import { RowsScreen } from './Rows'
import { SavedViewScreen } from './SavedViews'
import { SettingsScreen } from './Settings'
import { SummaryScreen } from './Summary'
import { TensorsScreen } from './Tensors'
import { ViewsScreen, pickViewTab } from './Views'
import { NewWorkbookSheet, WorkbookList, sortedWorkbooks } from './Workbooks'

const TABS = [
  { path: '/', label: '요약' },
  { path: '/rows', label: '거래' },
  { path: '/views', label: '보기' },
  { path: '/tensors', label: '텐서' },
  { path: '/grid', label: '표' },
  { path: '/settings', label: '설정' },
] as const

const SUB: Record<string, { title: string; parent: string }> = {
  '/budgets': { title: '예산', parent: '/settings' },
  '/items': { title: '항목·차원 관리', parent: '/settings' },
  '/csv': { title: 'CSV', parent: '/settings' },
}

function tabOf(path: string): string {
  if (SUB[path]) return SUB[path].parent
  if (path.startsWith('/tensors/')) return '/tensors'
  if (path.startsWith('/views/')) return '/views'
  return TABS.some((t) => t.path === path) ? path : '/'
}

function go(path: string): void {
  if (location.pathname !== path) navigate(path)
}

function Banner() {
  const connection = useT((s) => s.connection)
  if (connection === 'online') return null
  return (
    <div className="banner offline" role="status">
      {connection === 'offline' ? '오프라인입니다. 적은 것은 연결되면 보냅니다.' : '연결 중…'}
    </div>
  )
}

/** Opens the workbook kept for this device, or the first one, when the
    one on screen is gone (or nothing is open yet); not while an address
    names one (/w/<id>). */
function useOpenWorkbook(path: string): void {
  const current = useT((s) => s.current)
  const workbooks = useT((s) => s.workbooks)
  const ready = useT((s) => s.phase === 'ready')
  const named = path.startsWith('/w/')
  useEffect(() => {
    if (!ready || named || (current && workbooks[current])) return
    const kept = lastWorkbook()
    const pick: Id = workbooks[kept] ? kept : (sortedWorkbooks(workbooks)[0]?.id ?? 0)
    if (pick) engine.open(pick)
  }, [ready, named, current, workbooks])
}

/**
 * /w/<id>, /w/<id>?view=<tab> and /w/<id>/v/<view id or name> (design 00
 * 7): a link from talk's tensor card, or a saved view's address. Opens that
 * workbook, then the screen (the sign-in screen comes first when needed;
 * the address stays meanwhile). A workbook I may not see, or a view that
 * is not there, is said so.
 */
function useAddress(path: string): void {
  const m = /^\/w\/(\d+)(?:\/v\/([^/]+))?\/?$/.exec(path)
  const id: Id = m ? Number(m[1]) : 0
  const key = m?.[2] ?? null
  const wrong = path.startsWith('/w/') && !m
  const ready = useT((s) => s.phase === 'ready')
  const known = useT((s) => (id ? Boolean(s.workbooks[id]) : false))
  const book = useT((s) => (id ? s.books[id] : undefined))
  useEffect(() => {
    if (wrong) return navigate('/', { replace: true })
    if (!id || !ready) return
    if (!known) {
      showToast('열 수 없는 통합 문서입니다. 지워졌거나 나에게 공개되지 않았습니다.')
      return navigate('/', { replace: true })
    }
    engine.open(id)
    if (key === null) {
      const tab = new URLSearchParams(location.search).get('view')
      return navigate(tab && pickViewTab(tab) ? '/views' : '/', { replace: true })
    }
    if (!book) return
    let name = key
    try {
      name = decodeURIComponent(key)
    } catch {
      // as written
    }
    const v = findView(book.views, name)
    if (!v && book.loading) return // its views come with it
    if (!v) showToast(`없는 보기입니다: ${name}`)
    navigate(v ? `/views/${v.id}` : '/views', { replace: true })
  }, [wrong, id, key, ready, known, book])
}

function Screen({ path, month, setMonth }: { path: string; month: string; setMonth: (m: string) => void }) {
  const cur = useCurrent()
  const [creating, setCreating] = useState(false)
  if (path.startsWith('/w/')) return <Spinner /> // useAddress goes on from here
  if (path === '/settings') return <SettingsScreen cur={cur} />
  if (!cur) {
    return (
      <div className="empty">
        <p>아직 통합 문서가 없습니다.</p>
        <p className="small">통합 문서 하나에 집의 수입과 지출을 적습니다. 처음에는 나만 보고, 가족에게 공개할 수 있습니다.</p>
        <button type="button" className="primary" onClick={() => setCreating(true)}>
          첫 통합 문서 만들기
        </button>
        <NewWorkbookSheet open={creating} onClose={() => setCreating(false)} />
      </div>
    )
  }
  const props = { cur, month, setMonth }
  if (path === '/tensors' || path.startsWith('/tensors/')) return <TensorsScreen cur={cur} path={path} />
  const view = /^\/views\/(\d+)$/.exec(path)
  if (view) return <SavedViewScreen cur={cur} id={Number(view[1])} />
  switch (path) {
    case '/rows':
      return <RowsScreen {...props} />
    case '/views':
      return <ViewsScreen {...props} />
    case '/grid':
      return <GridScreen cur={cur} />
    case '/budgets':
      return <BudgetsScreen {...props} />
    case '/items':
      return <ItemsScreen cur={cur} />
    case '/csv':
      return <CsvScreen cur={cur} />
    default:
      return <SummaryScreen {...props} />
  }
}

function Ready() {
  const path = usePath()
  const wide = useMedia('(min-width: 900px)')
  const [month, setMonth] = useState(() => monthOf(Date.now()))
  const [picking, setPicking] = useState(false)
  const [creating, setCreating] = useState(false)
  const workbook = useT((s) => s.workbooks[s.current])
  useOpenWorkbook(path)
  useAddress(path)
  // a phone's keyboard: the bottom tabs make room, the back key that puts it away ends the typing
  useEffect(() => (hasCoarsePointer() ? watchKeyboard() : undefined), [])
  const tab = tabOf(path)
  const sub = SUB[path]

  const tabs = (
    <nav className={wide ? 'tabs t-top-tabs' : 't-bottom-tabs'} aria-label="화면">
      {TABS.map((t) => (
        <button key={t.path} type="button" aria-current={tab === t.path ? 'page' : undefined} onClick={() => go(t.path)}>
          {t.label}
        </button>
      ))}
    </nav>
  )

  return (
    <div className="app t-app">
      <Banner />
      <div className={wide ? 't-split' : 't-single'}>
        {wide && (
          <aside className="t-side">
            <div className="t-side-head">
              <img src="/icons/favicon.svg" alt="" width={28} height={28} />
              <strong>tensor</strong>
            </div>
            <WorkbookList onPick={(id) => engine.open(id)} onNew={() => setCreating(true)} />
          </aside>
        )}
        <main className="t-main">
          <header className="bar">
            {sub ? (
              <>
                <button type="button" className="icon-btn" aria-label="뒤로" onClick={() => goBack(sub.parent)}>
                  <Icon name="back" />
                </button>
                <h2>{sub.title}</h2>
              </>
            ) : wide ? (
              <h2 className="t-title">
                {workbook?.name ?? 'tensor'}
                {workbook && <VisibilityBadge workbook={workbook} />}
              </h2>
            ) : (
              <button type="button" className="t-title t-pick" onClick={() => setPicking(true)} aria-label="통합 문서 고르기">
                <span>{workbook?.name ?? 'tensor'}</span>
                {workbook && <VisibilityBadge workbook={workbook} />}
                <Icon name="down" size={18} />
              </button>
            )}
            <span className="spacer" />
            {wide && tabs}
          </header>
          <div className="t-body">
            <Screen path={path} month={month} setMonth={setMonth} />
          </div>
          {!wide && tabs}
        </main>
      </div>
      <Sheet open={picking} onClose={() => setPicking(false)} title="통합 문서">
        <WorkbookList
          onPick={(id) => {
            engine.open(id)
            setPicking(false)
          }}
          onNew={() => {
            setPicking(false)
            setCreating(true)
          }}
        />
      </Sheet>
      <NewWorkbookSheet open={creating} onClose={() => setCreating(false)} />
      <Toast />
    </div>
  )
}

export function App() {
  const phase = useT((s) => s.phase)
  if (phase === 'signed-out') {
    return (
      <>
        <LoginScreen />
        <Toast />
      </>
    )
  }
  if (phase === 'loading' && !getState().me) {
    return (
      <div className="app center">
        <Spinner />
      </div>
    )
  }
  return <Ready />
}
